import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  DestroyRef,
  ENVIRONMENT_INITIALIZER,
  inject,
  makeStateKey,
  ModuleWithProviders,
  NgModule,
  PLATFORM_ID,
  Provider,
  TransferState,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import locales from 'assets/locales.json';
import { distinctUntilChanged, forkJoin, map, Observable, of, startWith, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { EnvironmentService } from '@offconon/shared/utils/services/environment';

import { CustomLoader } from './custom-loader';
import { TranslateLoader } from './translate.loader';
import { TranslateService } from './translate.service';
import { mergeDeep } from './util';

function makeFactoryKey(id: string, lang: string) {
  const normalized = id.replace(/[\/\\]+/g, '_').replace(/^_|_$/g, '');
  return makeStateKey<any>(`tl_${normalized}_${lang}`);
}

const LANG_STORAGE_KEY = 'LOCALIZE_DEFAULT_LANGUAGE';

function getInitialLang(
  translate: TranslateService,
  platformId: Object,
  defaultLang: string,
): string {
  if (translate.currentLang) {
    return translate.currentLang;
  }

  if (isPlatformBrowser(platformId)) {
    const path = window.location.pathname;
    const segments = path.split('/').filter((s) => s.length > 0);
    const firstSegment = segments[0]?.toLowerCase();
    const supportedLangs: string[] = locales.locales ?? [];

    if (firstSegment && supportedLangs.includes(firstSegment)) {
      return firstSegment;
    }

    const storedLang = localStorage.getItem(LANG_STORAGE_KEY);
    if (storedLang && supportedLangs.includes(storedLang.toLowerCase())) {
      return storedLang.toLowerCase();
    }
  }

  if (translate.defaultLang) {
    return translate.defaultLang;
  }

  return defaultLang;
}

function normalizePath(path: string): string {
  return path.replace(/[\/]*$/, '') + '/';
}

function expandDotKeys(obj: any): any {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }

  const result: any = {};

  for (const key of Object.keys(obj)) {
    const value = obj[key];

    if (key.includes('.') && typeof value === 'string') {
      const parts = key.split('.');
      let current = result;

      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        if (!current[part] || typeof current[part] !== 'object') {
          current[part] = {};
        }
        current = current[part];
      }

      current[parts[parts.length - 1]] = value;
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      result[key] = expandDotKeys(value);
    } else {
      result[key] = value;
    }
  }

  return result;
}

function deepMergeTranslation(target: any, source: any): any {
  if (!target || typeof target !== 'object') {
    target = {};
  }

  const expandedSource = expandDotKeys(source);
  const result = { ...target };

  for (const key in expandedSource) {
    if (Object.prototype.hasOwnProperty.call(expandedSource, key)) {
      if (
        expandedSource[key] &&
        typeof expandedSource[key] === 'object' &&
        !Array.isArray(expandedSource[key]) &&
        result[key] &&
        typeof result[key] === 'object' &&
        !Array.isArray(result[key])
      ) {
        result[key] = deepMergeTranslation(result[key], expandedSource[key]);
      } else {
        result[key] = expandedSource[key];
      }
    }
  }

  return result;
}

export class MultiTranslateLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private paths: string[],
  ) {}

  getTranslation(lang: string): Observable<any> {
    if (!this.paths.length) {
      return of({});
    }

    const loaders = this.paths.map((path) => {
      const loader = new CustomLoader(this.http, path);
      return loader.getTranslation(lang).pipe(catchError(() => of({})));
    });

    return forkJoin(loaders).pipe(
      map((translations) => translations.reduce((acc, curr) => mergeDeep(acc, curr), {})),
    );
  }
}

@NgModule({})
export class TranslateLoaderModule {
  static forPaths(pathOrPaths: string | string[]): ModuleWithProviders<TranslateLoaderModule> {
    const paths = Array.isArray(pathOrPaths) ? pathOrPaths : [pathOrPaths];
    const normalizedPaths = paths.map(normalizePath);

    return {
      ngModule: TranslateLoaderModule,
      providers: [
        {
          provide: ENVIRONMENT_INITIALIZER,
          multi: true,
          useFactory: () => {
            const translate = inject(TranslateService);
            const http = inject(HttpClient);
            const destroyRef = inject(DestroyRef);
            const platformId = inject(PLATFORM_ID);
            const transferState = inject(TransferState);
            const loader = new MultiTranslateLoader(http, normalizedPaths);
            const factoryId = normalizedPaths.join('|');

            const loadTranslationForLang = (lang: string) =>
              loader.getTranslation(lang).pipe(
                catchError(() => of({})),
                map((translations) => ({ lang, translations })),
              );

            const initialLang = getInitialLang(translate, platformId, 'en');

            translate.onLangChange
              .pipe(
                startWith({ lang: initialLang }),
                map((event) => event.lang),
                distinctUntilChanged(),
                switchMap((lang) => {
                  const effectiveLang = lang ?? 'en';
                  if (isPlatformBrowser(platformId)) {
                    const key = makeFactoryKey(factoryId, effectiveLang);
                    if (transferState.hasKey(key)) {
                      const cached = transferState.get(key, null);
                      if (cached && Object.keys(cached).length > 0) {
                        return of({ lang: effectiveLang, translations: cached });
                      }
                    }
                  }
                  return loadTranslationForLang(effectiveLang);
                }),
                takeUntilDestroyed(destroyRef),
              )
              .subscribe(({ lang, translations }) => {
                if (translations && Object.keys(translations).length > 0) {
                  if (isPlatformServer(platformId)) {
                    const key = makeFactoryKey(factoryId, lang);
                    transferState.set(key, translations);
                  }
                  const existingTranslations = translate.translations[lang] || {};
                  const mergedTranslations = mergeDeep(existingTranslations, translations);
                  translate.setTranslation(lang, mergedTranslations, false);
                }
              });

            return () => undefined;
          },
        },
      ],
    };
  }
}

export function loadTranslate(
  pathOrPaths: string | string[],
): ModuleWithProviders<TranslateLoaderModule> {
  return TranslateLoaderModule.forPaths(pathOrPaths);
}

type ProvideDynamicTranslationOptions = {
  defaultLang?: string;
  merge?: boolean;
};

export function provideDynamicTranslation(
  folderPath: string,
  options: ProvideDynamicTranslationOptions = {},
): Provider {
  const normalisedFolder = normalizeFolder(folderPath);
  const defaultLang = options.defaultLang ?? 'en';

  return {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useFactory: () => {
      const translate = inject(TranslateService);
      const http = inject(HttpClient);
      const destroyRef = inject(DestroyRef);
      const platformId = inject(PLATFORM_ID);
      const transferState = inject(TransferState);
      const environmentService = inject(EnvironmentService, { optional: true });
      const loader = new CustomLoader(http, normalisedFolder, platformId, environmentService);

      translate.setDefaultLang(defaultLang);

      const loadTranslationForLang = (lang: string) =>
        loader.getTranslation(lang).pipe(
          catchError(() => of({})),
          map((translations) => ({ lang, translations })),
        );

      const initialLang = getInitialLang(translate, platformId, defaultLang);

      if (!translate.currentLang) {
        translate.currentLang = initialLang;
      }

      translate.onLangChange
        .pipe(
          startWith({ lang: initialLang }),
          map((event) => event.lang),
          distinctUntilChanged(),
          switchMap((lang) => {
            const effectiveLang = lang ?? defaultLang;
            if (isPlatformBrowser(platformId)) {
              const key = makeFactoryKey(normalisedFolder, effectiveLang);
              if (transferState.hasKey(key)) {
                const cached = transferState.get(key, null);
                if (cached && Object.keys(cached).length > 0) {
                  return of({ lang: effectiveLang, translations: cached });
                }
              }
            }
            return loadTranslationForLang(effectiveLang);
          }),
          takeUntilDestroyed(destroyRef),
        )
        .subscribe(({ lang, translations }) => {
          if (translations && Object.keys(translations).length > 0) {
            if (isPlatformServer(platformId)) {
              const key = makeFactoryKey(normalisedFolder, lang);
              transferState.set(key, translations);
            }
            const existingTranslations = translate.translations[lang] || {};
            const mergedTranslations = deepMergeTranslation(existingTranslations, translations);
            translate.setTranslation(lang, mergedTranslations, false);
          }
        });

      return () => undefined;
    },
  };
}

function normalizeFolder(folderPath: string): string {
  if (!folderPath) {
    return '';
  }
  const trimmed = folderPath.replace(/^\.?\/?assets\/translate\/?/i, '').replace(/^\/+|\/+$/g, '');
  return trimmed ? `${trimmed}/` : '';
}
